'use client';

import { ElementType, ReactNode, useRef, useState, type PointerEvent } from 'react';
import { Box } from '@mui/material';
import { haptic } from './haptics';

const TRIGGER = 72;
const MAX = 140;

export type SwipeSide = { label: string; Icon: ElementType; color: string };

type Props = {
  /** Revealed when the row is dragged to the right. */
  right?: SwipeSide | null;
  /** Revealed when the row is dragged to the left. */
  left?: SwipeSide | null;
  onSwipe: (direction: 'right' | 'left') => void;
  children: ReactNode;
};

/** A touch row that runs one action when swiped right and another when swiped left. Mouse drags are ignored. */
export default function SwipeActions({ right, left, onSwipe, children }: Props) {
  const content = useRef<HTMLDivElement>(null);
  const gesture = useRef<{ id: number; x: number; y: number; dx: number; direction: 'right' | 'left' | null } | null>(null);
  const suppressClick = useRef(false);
  const [armed, setArmed] = useState(false);
  const [dragging, setDragging] = useState<'right' | 'left' | null>(null);

  const move = (dx: number, animate: boolean) => {
    if (!content.current) return;
    content.current.style.transition = animate ? 'transform 220ms cubic-bezier(0.2, 0.8, 0.2, 1)' : 'none';
    content.current.style.transform = dx ? `translate3d(${dx}px, 0, 0)` : '';
  };

  const onPointerDown = (event: PointerEvent) => {
    if ((!right && !left) || event.pointerType === 'mouse') return;
    gesture.current = { id: event.pointerId, x: event.clientX, y: event.clientY, dx: 0, direction: null };
  };

  const onPointerMove = (event: PointerEvent) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    const dx = event.clientX - g.x;
    const dy = event.clientY - g.y;
    if (g.direction === null) {
      if (Math.abs(dx) < 8 && Math.abs(dy) < 8) return;
      const direction = dx > 0 ? 'right' : 'left';
      if (Math.abs(dx) <= Math.abs(dy) || !(direction === 'right' ? right : left)) {
        gesture.current = null;
        return;
      }
      g.direction = direction;
      (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
      setDragging(direction);
    }
    const distance = Math.max(0, g.direction === 'right' ? dx : -dx);
    const eased = distance > TRIGGER ? TRIGGER + Math.min(MAX - TRIGGER, (distance - TRIGGER) * 0.4) : distance;
    g.dx = g.direction === 'right' ? eased : -eased;
    move(g.dx, false);
    const nextArmed = eased >= TRIGGER;
    if (nextArmed !== armed) {
      setArmed(nextArmed);
      haptic();
    }
  };

  const finish = () => {
    const g = gesture.current;
    gesture.current = null;
    if (!g?.direction) return;
    suppressClick.current = true;
    move(0, true);
    setDragging(null);
    setArmed(false);
    if (Math.abs(g.dx) >= TRIGGER) onSwipe(g.direction);
  };

  const side = dragging === 'right' ? right : dragging === 'left' ? left : null;
  return (
    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
      {side && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: dragging === 'right' ? 'flex-start' : 'flex-end',
            gap: 1,
            px: 2.5,
            bgcolor: armed ? side.color : '#c9c9c4',
            color: '#fff',
            fontSize: '0.8125rem',
            fontWeight: 600,
            transition: 'background-color 150ms ease',
          }}
        >
          <side.Icon sx={{ fontSize: 22, transform: armed ? 'scale(1.1)' : 'scale(0.9)', transition: 'transform 150ms ease' }} />
          {side.label}
        </Box>
      )}
      <Box
        ref={content}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={finish}
        onPointerCancel={finish}
        onClickCapture={(event) => {
          if (!suppressClick.current) return;
          suppressClick.current = false;
          event.preventDefault();
          event.stopPropagation();
        }}
        sx={{ position: 'relative', touchAction: 'pan-y', willChange: dragging ? 'transform' : undefined }}
      >
        {children}
      </Box>
    </Box>
  );
}
